import { SellarImportsPayoutUpdateRequestType } from "@sellar-io/api-v2/types";
import { Alert } from "../../../../../components/alert/Alert";
import { InputLabel, InputContainer } from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../../../components/modal/Dialog";
import { apiV2AssertError } from "../../../../../hooks/api/v2/apiV2Request";
import { useForm, useWatch } from "react-hook-form";
import { useEffect } from "react";
import {
  Button,
  InputOption,
  InputSelect,
  InputDate,
  InputNumber,
  InputCurrency,
} from "@sellar-io/components";
import { useSellarImportPayoutUpdate } from "../hooks/useSellarImportPayoutUpdate";
import dayjs from "dayjs";

export const UpdateSellarPayoutDialog = ({
  active,
  onDismiss,
  existingPayout,
}: {
  active: boolean;
  onDismiss: () => void;
  existingPayout: SellarImportsPayoutUpdateRequestType & { id: number };
}) => {
  const { reset, ...formHook } = useForm<
    Omit<
      SellarImportsPayoutUpdateRequestType,
      "captureStart" | "captureEnd"
    > & {
      captureStart: string;
      captureEnd: string;
    }
  >({
    shouldUseNativeValidation: true,
  });

  useEffect(() => {
    if (active) {
      reset({
        ...existingPayout,

        // convert pence fee into GBP
        draughtHandlingFee: existingPayout.draughtHandlingFee / 100,
        packagedHandlingFee: existingPayout.packagedHandlingFee / 100,

        // format dates
        captureStart: dayjs(existingPayout.captureStart).format("YYYY-MM-DD"),
        captureEnd: dayjs(existingPayout.captureEnd).format("YYYY-MM-DD"),
      });
    }
  }, [active, existingPayout, reset]);

  const { captureStart, captureEnd } = useWatch({
    control: formHook.control,
  });

  const sellarImportPayoutUpdateMutation = useSellarImportPayoutUpdate(
    existingPayout.id,
  );

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        title={"Update payout"}
        description={"Update payout capture window, status and associated fees"}
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
      />
      <form
        onSubmit={formHook.handleSubmit((data) => {
          const draughtHandlingFeePence = data.draughtHandlingFee * 100;
          const packagedHandlingFeePence = data.packagedHandlingFee * 100;

          sellarImportPayoutUpdateMutation.mutate(
            {
              ...data,
              captureStart: dayjs(data.captureStart).toDate(),
              captureEnd: dayjs(data.captureEnd).toDate(),
              draughtHandlingFee: draughtHandlingFeePence,
              packagedHandlingFee: packagedHandlingFeePence,
            },
            {
              onSuccess: () => onDismiss(),
            },
          );
        })}
      >
        <DialogContent>
          {sellarImportPayoutUpdateMutation.isError ? (
            <Alert
              type={"error"}
              message={
                apiV2AssertError(sellarImportPayoutUpdateMutation.error)
                  ?.response?.data.message ?? "Unknown error occurred."
              }
            />
          ) : null}

          <InputLabel>Payout Status</InputLabel>
          <InputContainer>
            <InputSelect {...formHook.register("status", { required: true })}>
              <InputOption value={"draft"}>Draft</InputOption>
              <InputOption value={"paid"}>Paid</InputOption>
              <InputOption value={"processing"}>Processing</InputOption>
            </InputSelect>
          </InputContainer>

          <InputLabel>Recurring Customer Fee (%)</InputLabel>
          <InputContainer>
            <InputNumber
              {...formHook.register("recurringCustomerFeePercentage", {
                required: true,
                min: 0,
                valueAsNumber: true,
              })}
              step={"0.01"}
            />
          </InputContainer>

          <InputLabel>Draught handling Fee (£)</InputLabel>
          <InputContainer>
            <InputCurrency
              showPrefix
              allowNegative
              {...formHook.register("draughtHandlingFee", {
                required: true,
                min: 0,
                valueAsNumber: true,
              })}
            />
          </InputContainer>

          <InputLabel>Packaged handling Fee (£)</InputLabel>
          <InputContainer>
            <InputCurrency
              showPrefix
              allowNegative
              {...formHook.register("packagedHandlingFee", {
                required: true,
                min: 0,
                valueAsNumber: true,
              })}
            />
          </InputContainer>

          <InputLabel>Capture start date</InputLabel>
          <InputContainer>
            <InputDate
              {...formHook.register("captureStart", {
                required: true,
                valueAsDate: true,
                value: captureStart,
              })}
            />
          </InputContainer>

          <InputLabel>Capture end date</InputLabel>
          <InputContainer>
            <InputDate
              {...formHook.register("captureEnd", {
                required: true,
                valueAsDate: true,
                value: captureEnd,
              })}
            />
          </InputContainer>
        </DialogContent>
        <DialogCTA>
          <Button
            loading={sellarImportPayoutUpdateMutation.isPending}
            type="submit"
          >
            Update
          </Button>
        </DialogCTA>
      </form>
    </Dialog>
  );
};
